/**
 * @file validators.ts
 * @role THE trust boundary. Every backend response is validated here (Zod) before any
 *       gameplay code uses it. These schemas are the single source of truth for the
 *       backend contract; the DTO types in dto.ts are inferred from them, so a schema edit
 *       updates the type automatically (no drift). Past the validator, data is trusted;
 *       before it, treat it as hostile.
 * @owns The Zod schemas + validate functions for session + spin responses.
 * @reads Raw (unknown) backend payloads.
 * @neverDoes Decide outcomes. Trust unvalidated data. Touch Pixi.
 * @invariants 7 (money is integer minor units — enforced as .int()), 10 (validate before use).
 * @see docs/BACKEND_CONTRACT.md, src/core/backend/dto.ts
 */
import { z } from 'zod';
import { GameError } from '@core/errors/GameError';
import type { SpinRequestDto } from './dto';

// Money is always integer minor units (Invariant 7); balances/wins are non-negative.
const nonNegMinor = z.number().int().safe().nonnegative();

export const GridPositionSchema = z.object({
  reel: z.number().int().nonnegative(),
  row: z.number().int().nonnegative(),
});

export const WinSchema = z.object({
  winId: z.string().min(1),
  type: z.enum(['line', 'scatter', 'ways', 'bonus', 'other']),
  amountMinor: nonNegMinor,
  positions: z.array(GridPositionSchema),
  symbolId: z.string().optional(),
  lineId: z.number().int().optional(),
});

/** One server-resolved sub-spin of a feature (e.g. a single free spin), replayed by the client. */
export const FeatureSpinSchema = z.object({
  grid: z.array(z.array(z.string())),
  wins: z.array(WinSchema),
  totalWinMinor: nonNegMinor,
});

export const FeatureTriggerSchema = z.object({
  featureId: z.string().min(1),
  type: z.enum(['freeSpins', 'bonusGame', 'pickBonus', 'respin']),
  triggerPositions: z.array(GridPositionSchema),
  /** The whole feature, already resolved server-side. The client REPLAYS these in order; it
   *  never decides how many there are or what they contain (Invariant 1). */
  spins: z.array(FeatureSpinSchema).default([]),
  /** The feature's own subtotal — excludes the triggering base-game win. */
  totalWinMinor: nonNegMinor.default(0),
});

export const SpinResultSchema = z.object({
  requestId: z.string().min(1),
  sessionId: z.string().min(1),
  roundId: z.string().min(1),
  transactionId: z.string().min(1),
  gameId: z.string().min(1),
  currency: z.string().length(3),
  betAmountMinor: nonNegMinor,
  balanceBeforeMinor: nonNegMinor,
  balanceAfterMinor: nonNegMinor,
  totalWinMinor: nonNegMinor,
  /** Cash rounds debit the configured stake. Campaign rounds are funded by an external grant. */
  roundFunding: z.enum(['cash', 'campaign']).optional(),
  campaignRoundsRemaining: z.number().int().nonnegative().nullable().optional(),
  promoFreeSpinsTriggered: z.number().int().positive().nullable().optional(),
  grid: z.array(z.array(z.string())),
  wins: z.array(WinSchema),
  featuresTriggered: z.array(FeatureTriggerSchema),
  serverTime: z.string().min(1),
});

export const CreateSessionResponseSchema = z.object({
  sessionId: z.string().min(1),
  gameId: z.string().min(1),
  currency: z.string().length(3),
  balanceMinor: nonNegMinor,
  betAmountMinor: nonNegMinor.optional(),
  activeCampaignRoundsRemaining: z.number().int().nonnegative().nullable().optional(),
  pendingPromoFreeSpinsCount: z.number().int().positive().nullable().optional(),
});

export const RestoreSessionResponseSchema = CreateSessionResponseSchema.extend({
  hasOpenRound: z.boolean(),
  /** The already-settled result of the unresolved round, present iff hasOpenRound. The
   *  client REPLAYS it (grid + balance + win presentation); it never re-decides anything. */
  openRound: SpinResultSchema.optional(),
});

export interface SpinResultValidationContext {
  request: SpinRequestDto;
  session: { sessionId: string; gameId: string; currency: string };
  columns: number;
  rows: number;
  symbolIds: readonly string[];
}

/** Validate a spin result. Invalid -> SPIN_RESPONSE_INVALID (routes the flow to error). */
export function validateSpinResult(raw: unknown): z.infer<typeof SpinResultSchema> {
  const parsed = SpinResultSchema.safeParse(raw);
  if (!parsed.success) {
    throw new GameError('SPIN_RESPONSE_INVALID', `Invalid spin result: ${parsed.error.message}`, {
      cause: parsed.error,
    });
  }
  return parsed.data;
}

/**
 * Shape validation plus game-specific semantic checks. This is what spin flow should use:
 * the schema proves the payload is well-formed, then this proves it belongs to the active
 * request/session/reel setup before any gameplay code treats it as authoritative.
 */
export interface RoundShapeContext {
  columns: number;
  rows: number;
  symbolIds: readonly string[];
}

/**
 * Request-independent sanity checks shared by fresh spin results and restored open rounds:
 * grid shape, symbol membership, win/feature positions, win-total sum, balance arithmetic.
 * The arithmetic here is trust-boundary enforcement (prove the server's numbers are
 * internally consistent before adopting them), NOT client-side balance computation — the
 * adopted value is always the server's own balanceAfterMinor (Invariant 4).
 */
function collectRoundShapeErrors(
  result: z.infer<typeof SpinResultSchema>,
  context: RoundShapeContext,
): string[] {
  const errors: string[] = [];
  const symbols = new Set(context.symbolIds);

  /** Shape + symbol-membership for any grid: the base screen or a feature sub-spin's. */
  const collectGridErrors = (grid: readonly (readonly string[])[], label: string): void => {
    if (grid.length !== context.columns) {
      errors.push(`${label} columns expected ${context.columns}, got ${grid.length}`);
    }
    grid.forEach((column, reel) => {
      if (column.length !== context.rows) {
        errors.push(`${label}[${reel}] rows expected ${context.rows}, got ${column.length}`);
      }
      column.forEach((symbolId, row) => {
        if (!symbols.has(symbolId)) {
          errors.push(`unknown ${label} symbol ${symbolId} at ${reel}:${row}`);
        }
      });
    });
  };

  collectGridErrors(result.grid, 'grid');

  const inGrid = (reel: number, row: number): boolean =>
    reel >= 0 && reel < context.columns && row >= 0 && row < context.rows;

  for (const win of result.wins) {
    if (win.symbolId && !symbols.has(win.symbolId))
      errors.push(`unknown win symbol ${win.symbolId}`);
    for (const position of win.positions) {
      if (!inGrid(position.reel, position.row)) {
        errors.push(`win position outside grid ${position.reel}:${position.row}`);
      }
    }
  }

  for (const feature of result.featuresTriggered) {
    for (const position of feature.triggerPositions) {
      if (!inGrid(position.reel, position.row)) {
        errors.push(`feature position outside grid ${position.reel}:${position.row}`);
      }
    }

    // Every sub-spin the client is about to replay gets the same scrutiny as the base screen —
    // a feature screen is shown on the same reels and must be equally trustworthy (Invariant 5).
    feature.spins.forEach((spin, index) => {
      const label = `${feature.featureId} spin ${index}`;
      collectGridErrors(spin.grid, label);
      for (const win of spin.wins) {
        if (win.symbolId && !symbols.has(win.symbolId)) {
          errors.push(`unknown ${label} win symbol ${win.symbolId}`);
        }
        for (const position of win.positions) {
          if (!inGrid(position.reel, position.row)) {
            errors.push(`${label} win position outside grid ${position.reel}:${position.row}`);
          }
        }
      }
      const spinTotal = spin.wins.reduce((sum, win) => sum + win.amountMinor, 0);
      if (spin.totalWinMinor !== spinTotal) {
        errors.push(`${label} totalWinMinor expected ${spinTotal}, got ${spin.totalWinMinor}`);
      }
    });

    const featureTotal = feature.spins.reduce((sum, spin) => sum + spin.totalWinMinor, 0);
    if (feature.spins.length > 0 && feature.totalWinMinor !== featureTotal) {
      errors.push(
        `${feature.featureId} totalWinMinor expected ${featureTotal}, got ${feature.totalWinMinor}`,
      );
    }
  }

  // The round total folds in every feature subtotal on top of the base-game wins, mirroring the
  // server (ReelCore SlotGameBase: Win is base-only, TotalWin adds each feature's subtotal).
  const baseWinsTotal = result.wins.reduce((sum, win) => sum + win.amountMinor, 0);
  const featuresTotal = result.featuresTriggered.reduce(
    (sum, feature) => sum + feature.totalWinMinor,
    0,
  );
  const winsTotal = baseWinsTotal + featuresTotal;
  if (result.totalWinMinor !== winsTotal) {
    errors.push(`totalWinMinor expected ${winsTotal}, got ${result.totalWinMinor}`);
  }
  const wagerDebit = result.roundFunding === 'campaign' ? 0 : result.betAmountMinor;
  const expectedBalanceAfter = result.balanceBeforeMinor - wagerDebit + result.totalWinMinor;
  if (result.balanceAfterMinor !== expectedBalanceAfter) {
    errors.push(
      `balanceAfterMinor expected ${expectedBalanceAfter}, got ${result.balanceAfterMinor}`,
    );
  }
  return errors;
}

export function validateSpinResultForContext(
  raw: unknown,
  context: SpinResultValidationContext,
): z.infer<typeof SpinResultSchema> {
  const result = validateSpinResult(raw);
  const errors: string[] = [];

  if (result.requestId !== context.request.requestId) errors.push('requestId mismatch');
  if (result.sessionId !== context.session.sessionId) errors.push('sessionId mismatch');
  if (result.gameId !== context.session.gameId || result.gameId !== context.request.gameId) {
    errors.push('gameId mismatch');
  }
  if (
    result.currency !== context.session.currency ||
    result.currency !== context.request.currency
  ) {
    errors.push('currency mismatch');
  }
  if (result.betAmountMinor !== context.request.betAmountMinor) errors.push('bet mismatch');

  errors.push(...collectRoundShapeErrors(result, context));

  if (errors.length > 0) {
    throw new GameError('SPIN_RESPONSE_INVALID', `Invalid spin result: ${errors.join('; ')}`);
  }
  return result;
}

/**
 * Validate a session-restore response, including the linkage between the envelope and an
 * open round: the round must belong to the restored session and the reported balance must
 * equal the round's settled balance, so a replay can never show a state the server did not
 * produce. The open round itself passes the same shape checks as a fresh spin result.
 */
export function validateRestoreSession(
  raw: unknown,
  context: RoundShapeContext,
): z.infer<typeof RestoreSessionResponseSchema> {
  const parsed = RestoreSessionResponseSchema.safeParse(raw);
  if (!parsed.success) {
    throw new GameError(
      'SESSION_CREATE_FAILED',
      `Invalid session restore response: ${parsed.error.message}`,
      { cause: parsed.error },
    );
  }
  const restored = parsed.data;
  const errors: string[] = [];

  if (restored.hasOpenRound && !restored.openRound) {
    errors.push('hasOpenRound is set but no open round data was provided');
  }
  if (!restored.hasOpenRound && restored.openRound) {
    errors.push('open round data provided without hasOpenRound');
  }
  const round = restored.openRound;
  if (restored.hasOpenRound && round) {
    if (round.sessionId !== restored.sessionId) errors.push('open round sessionId mismatch');
    if (round.gameId !== restored.gameId) errors.push('open round gameId mismatch');
    if (round.currency !== restored.currency) errors.push('open round currency mismatch');
    if (round.balanceAfterMinor !== restored.balanceMinor) {
      errors.push(
        `session balance ${restored.balanceMinor} disagrees with open round settlement ${round.balanceAfterMinor}`,
      );
    }
    errors.push(...collectRoundShapeErrors(round, context));
  }

  if (errors.length > 0) {
    throw new GameError('SESSION_CREATE_FAILED', `Invalid session restore: ${errors.join('; ')}`);
  }
  return restored;
}

export function validateCreateSession(raw: unknown): z.infer<typeof CreateSessionResponseSchema> {
  const parsed = CreateSessionResponseSchema.safeParse(raw);
  if (!parsed.success) {
    throw new GameError(
      'SESSION_CREATE_FAILED',
      `Invalid session response: ${parsed.error.message}`,
      {
        cause: parsed.error,
      },
    );
  }
  return parsed.data;
}

// ---------------------------------------------------------------------------
// RGS wire contract
//
// The schemas above describe the shape gameplay code consumes. The ones below describe what the
// RGS actually puts on the wire — a different shape (decimal money, `winDescription` entries
// discriminated by `type`, no grid positions). HttpRgsClient validates HERE first, then adapts.
// Validating the raw payload rather than our own adapter output is the point: the trust boundary
// has to sit at the true edge, or it is only checking our own work.
//
// See docs/RGS_SERVER_REQUIREMENTS.md and tests/fixtures/rgs/.
// ---------------------------------------------------------------------------

/** Server money: decimal major units, inconsistent scale (`1.00`, `1.0`, `1000` all observed). */
const wireDecimal = z.number().finite().nonnegative();
/** Integer minor units, sent only once the server implements A4. */
const wireMinor = z.number().int().safe().nonnegative();

/** `screen[reel][row]` — reel 0 leftmost, row 0 top. Matches our grid orientation. */
export const RgsScreenSchema = z.array(z.array(z.string().min(1)));

export const RgsPaylineWinSchema = z.object({
  type: z.literal('paylineWin'),
  paylineId: z.string().min(1),
  symbolId: z.string().min(1),
  symbolCount: z.number().int().positive(),
  win: wireDecimal,
});

/**
 * One feature step resolved by ReelCore. `freeSpinsAmount` is the total award known at this step
 * (it can grow after a retrigger), while `freeSpinsCount` is this step's 1-based position.
 */
export const RgsFeatureWinStepSchema = z.object({
  freeSpinsAmount: z.number().int().positive(),
  freeSpinsCount: z.number().int().positive(),
  totalWin: wireDecimal,
  screen: RgsScreenSchema,
  winDescription: z.array(RgsPaylineWinSchema),
});

/** A feature resolved entirely server-side in one response. Retriggers extend `data`. */
export const RgsFeatureWinSchema = z.object({
  type: z.literal('featureWin'),
  name: z.string().min(1),
  data: z.array(RgsFeatureWinStepSchema),
  totalWin: wireDecimal,
});

export const RgsWinDescriptionEntrySchema = z.discriminatedUnion('type', [
  RgsPaylineWinSchema,
  RgsFeatureWinSchema,
]);

export const RgsSpinResponseSchema = z
  .object({
    sequenceNumber: z.number().int().nonnegative(),
    betAmount: wireDecimal,
    /** The round TOTAL — base game plus every feature subtotal. */
    winAmount: wireDecimal,
    balance: wireDecimal,
    screen: RgsScreenSchema,
    winDescription: z.array(RgsWinDescriptionEntrySchema),
    state: z.unknown(),
    createdAt: z.string().min(1),
    // A2/A4 — absent until the server ships them; the adapter degrades gracefully.
    roundId: z.string().min(1).optional(),
    requestId: z.string().min(1).optional(),
    transactionId: z.string().min(1).optional(),
    sessionId: z.string().min(1).optional(),
    currency: z.string().length(3).optional(),
    currencyExponent: z.number().int().min(0).max(4).optional(),
    balanceBefore: wireDecimal.optional(),
    betAmountMinor: wireMinor.optional(),
    winAmountMinor: wireMinor.optional(),
    balanceBeforeMinor: wireMinor.optional(),
    balanceAfterMinor: wireMinor.optional(),
    campaignRoundsRemaining: z.number().int().nonnegative().nullable().optional(),
    promoFreeSpinsTriggered: z.number().int().positive().nullable().optional(),
  })
  .passthrough();

export const RgsSessionResponseSchema = z
  .object({
    id: z.string().min(1),
    gameCode: z.string().min(1),
    brand: z.string().min(1),
    userId: z.string().min(1),
    currency: z.string().length(3),
    status: z.string().min(1),
    betAmount: wireDecimal,
    balance: wireDecimal,
    betLevels: z.array(wireDecimal),
    paylines: z.array(
      z.object({ id: z.string().min(1), rowPerReel: z.array(z.number().int().nonnegative()) }),
    ),
    lastScreen: RgsScreenSchema.nullable(),
    lastWinDescription: z.array(RgsWinDescriptionEntrySchema),
    createdAt: z.string().min(1),
    updatedAt: z.string().min(1),
    state: z.unknown(),
    currencyExponent: z.number().int().min(0).max(4).optional(),
    balanceMinor: wireMinor.optional(),
    betAmountMinor: wireMinor.optional(),
    activeCampaignRoundsRemaining: z.number().int().nonnegative().nullable().optional(),
    pendingPromoFreeSpinsCount: z.number().int().positive().nullable().optional(),
  })
  .passthrough();

export const RgsHistoryResponseSchema = z
  .object({
    items: z.array(RgsSpinResponseSchema),
    page: z.number().int().positive(),
    pageSize: z.number().int().positive(),
    totalCount: z.number().int().nonnegative(),
    totalPages: z.number().int().nonnegative(),
  })
  .passthrough();

/** RFC 7807, as returned by the RGS DomainExceptionHandler for every domain failure. */
export const RgsProblemDetailsSchema = z
  .object({
    title: z.string().optional(),
    status: z.number().int().optional(),
    detail: z.string().optional(),
  })
  .passthrough();

export type RgsSpinResponse = z.infer<typeof RgsSpinResponseSchema>;
export type RgsSessionResponse = z.infer<typeof RgsSessionResponseSchema>;
export type RgsPaylineWin = z.infer<typeof RgsPaylineWinSchema>;
export type RgsFeatureWin = z.infer<typeof RgsFeatureWinSchema>;
export type RgsHistoryResponse = z.infer<typeof RgsHistoryResponseSchema>;

export function validateRgsSpinResponse(raw: unknown): RgsSpinResponse {
  const parsed = RgsSpinResponseSchema.safeParse(raw);
  if (!parsed.success) {
    throw new GameError(
      'SPIN_RESPONSE_INVALID',
      `Invalid RGS spin response: ${parsed.error.message}`,
      {
        cause: parsed.error,
      },
    );
  }
  return parsed.data;
}

export function validateRgsSessionResponse(raw: unknown): RgsSessionResponse {
  const parsed = RgsSessionResponseSchema.safeParse(raw);
  if (!parsed.success) {
    throw new GameError(
      'SESSION_CREATE_FAILED',
      `Invalid RGS session response: ${parsed.error.message}`,
      { cause: parsed.error },
    );
  }
  return parsed.data;
}

export function validateRgsHistoryResponse(raw: unknown): RgsHistoryResponse {
  const parsed = RgsHistoryResponseSchema.safeParse(raw);
  if (!parsed.success) {
    throw new GameError(
      'SPIN_RESPONSE_INVALID',
      `Invalid RGS history response: ${parsed.error.message}`,
      { cause: parsed.error },
    );
  }
  return parsed.data;
}
